/**
 * Dashboard Page - react-grid-layout edition
 * Widgets are draggable/resizable; layout is persisted to localStorage.
 * Filters open from a top-right button into a dropdown panel.
 * Rows: 1) KPI cards  2) Primary visualisations  3) AI-insight cards
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Badge,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import SyncIcon from '@mui/icons-material/Sync';
import useApi from '../../hooks/useApi';
import { useProjectProgress, CHANGE_TYPES } from '../../contexts/ProjectProgressContext';
import { useCustomAlert, useErrorToast } from '../../components/common';
import { formatBusinessText } from '../../utils/textFormatting';
import { isDatePickerFilterType } from '../../utils/filterTypes';
import { GLOBAL_MARGINS, GLOBAL_SPACING } from '../../utils/globalStyles';
import { exportWidgetsToPdf } from '../../utils/exportDashboardPdf';
import { pivotStackedBarLongRows } from '../../utils/barChartFormat';
import { getApiErrorMessage } from '../../utils/errorMessages';
import WidgetResultsRenderer from '../../components/Widgets/WidgetResultsRenderer';
import OutputModeSwitcher from '../../components/Widgets/OutputModeSwitcher';
import AiExecutionLoader, { RotatingStatusMessage } from '../../components/Widgets/AiExecutionLoader';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { DEFAULT_OUTPUT_MODE } from '../../constants/outputModes';
import { DEFAULT_PROMPT_TYPE } from '../../constants/promptTypes';
import { parseAiWidgetConfig, mergeAiConfigIntoQuery } from '../../utils/aiWidgetConfig';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantDefaultAiModel } from '../../hooks/useTenantDefaultAiModel';
import { isMyDashboardsOnlyAccess } from '../../constants/dashboardAccessPermissions';
import './DashboardPage.css';

function buildFolderTree(flatList) {
  if (!flatList || !Array.isArray(flatList)) return [];
  const byId = {};
  flatList.forEach((f) => {
    byId[f.FolderId] = { ...f, children: [] };
  });
  const roots = [];
  flatList.forEach((f) => {
    const node = byId[f.FolderId];
    if (!f.ParentFolderId || !byId[f.ParentFolderId]) {
      roots.push(node);
    } else {
      byId[f.ParentFolderId].children.push(node);
    }
  });
  roots.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0) || (a.FolderName || '').localeCompare(b.FolderName || ''));
  roots.forEach((r) => r.children.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0) || (a.FolderName || '').localeCompare(b.FolderName || '')));
  return roots;
}

function flattenFolders(nodes, list = []) {
  nodes.forEach((n) => {
    list.push(n);
    if (n.children?.length) flattenFolders(n.children, list);
  });
  return list;
}

const COLORS = [
  'rgb(64, 121, 141)',
  'rgb(209, 185, 167)',
  'rgb(149, 198, 213)',
  'rgb(145, 89, 145)',
  'rgb(165, 198, 165)',
  'rgb(125, 111, 100)',
  'rgb(170, 188, 244)',
  'rgb(73, 119, 73)',
  'rgb(208, 181, 208)',
  'rgb(102, 117, 121)',
];

const ACCENT = 'rgb(64,121,141)';

const GRID_MARGIN = [6, 6];
// rowHeight not forced — library default (150 px) is used.
// h values are therefore in 150 px units: h=1 → 150 px, h=2 → 306 px, etc.
/** v6: prior caches could desync from widget keys → RGL defaulted every tile to 1×1 (stacked left). */
const LAYOUT_KEY = (pid) => `dashGridLayout_v6_${pid}`;

/** Used for SSR / pre-measure fallback only; live width comes from `useContainerWidth`. */
const MIN_GRID_WIDTH = 560;

/** API bar rows: { name, value } (1–2 cols) or { …dimensions, count } (3+ cols). Avoid using the first numeric dimension (e.g. QuestionNumber: 1) as the bar value. */
const CHART_MEASURE_KEYS_LOWER = new Set([
  'value', 'count', 'total', 'sum', 'avg', 'average', 'amount', 'y', 'metric', 'median', 'min', 'max',
]);
const CHART_ID_LIKE_KEYS_LOWER = new Set([
  'id', 'rowid', 'index', 'responseid', 'projectid', 'widgetid', 'resultid', 'questionid', 'questionnumber',
]);

function extractChartNumericValue(item) {
  if (!item || typeof item !== 'object') return 0;
  const keys = Object.keys(item);
  const lowerToKey = new Map(keys.map((k) => [k.toLowerCase(), k]));
  const preferredOrder = ['value', 'count', 'total', 'sum', 'avg', 'average', 'amount', 'y', 'metric'];
  for (const pk of preferredOrder) {
    const k = lowerToKey.get(pk);
    if (k === undefined) continue;
    const n = Number(item[k]);
    if (Number.isFinite(n)) return n;
  }
  for (const k of keys) {
    const lk = k.toLowerCase();
    if (CHART_MEASURE_KEYS_LOWER.has(lk) || CHART_ID_LIKE_KEYS_LOWER.has(lk)) continue;
    const v = item[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  for (const k of keys) {
    const lk = k.toLowerCase();
    if (CHART_MEASURE_KEYS_LOWER.has(lk) || CHART_ID_LIKE_KEYS_LOWER.has(lk)) continue;
    const v = item[k];
    if (v != null && typeof v !== 'object' && String(v).trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function extractBarChartCategoryLabel(item) {
  if (!item || typeof item !== 'object') return 'Unknown';
  const n = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
  if (n(item.name)) return formatBusinessText(item.name);
  if (n(item.label)) return formatBusinessText(item.label);
  if (n(item.category)) return formatBusinessText(item.category);

  const skip = new Set([...CHART_MEASURE_KEYS_LOWER, ...CHART_ID_LIKE_KEYS_LOWER]);
  const parts = [];
  for (const k of Object.keys(item)) {
    if (skip.has(k.toLowerCase())) continue;
    const v = item[k];
    if (v == null || typeof v === 'object') continue;
    const s = String(v).trim();
    if (s !== '') parts.push(s);
  }
  if (parts.length) return formatBusinessText(parts.join(' · '));
  return 'Unknown';
}

/** True when any filter field has a real value (used to choose filtered vs saved dashboard widgets). */
function hasActiveDashboardFilters(filtersObj) {
  if (!filtersObj || typeof filtersObj !== 'object') return false;
  return Object.values(filtersObj).some((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return !!(v.from || v.to || v.fromDate || v.toDate);
    return String(v).trim() !== '';
  });
}

/** Deep clone filter state (values are plain JSON shapes). */
function cloneDashboardFilters(obj) {
  if (!obj || typeof obj !== 'object') return {};
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return { ...obj };
  }
}

const DASHBOARD_AI_WIDGET_TYPES = new Set([
  'recommendation',
  'major_issues',
  'summary',
  'success',
  'custom_ai_card',
  'analysis',
  'custom_ai',
]);

function isDashboardAiWidgetType(w) {
  return DASHBOARD_AI_WIDGET_TYPES.has((w?.widgetType || '').toLowerCase());
}


/**
 * After layout commit, one animation frame later counts as one “widget rendered” unit
 * (charts have laid out; percentage matches real paint milestones, not a timer).
 */

/* ─── Layout helpers ────────────────────────────────────────────────────────── */

/** Stable key for a widget (string for react-grid-layout). */
const wKey = (w, i) => String(w.widgetId ?? `w-${i}`);

/** react-grid-layout gives missing keys w:1 h:1; reject stale layout when widget set / ids change. */
const layoutKeysAlign = (layout, widgets) => {
  if (!widgets?.length) return Array.isArray(layout) && layout.length === 0;
  if (!Array.isArray(layout) || layout.length !== widgets.length) return false;
  const expected = new Set(widgets.map((w, i) => wKey(w, i)));
  const actual = new Set(layout.map((l) => l.i));
  if (expected.size !== actual.size) return false;
  for (const k of expected) {
    if (!actual.has(k)) return false;
  }
  return true;
};

/**
 * Classify a widget into one of three visual rows:
 *   'kpi'    → compact metric cards (row 1)
 *   'visual' → bar/pie/map charts (middle rows)
 *   'ai'     → AI-insight cards (last rows)
 */
const wCategory = (w) => {
  if (w.hasResults || isDashboardAiWidgetType(w)) return 'ai';
  if (w.widgetType === 'kpi') return 'kpi';
  return 'visual';
};

const isPieWidget = (w) => {
  const t = (w.widgetType || '').toLowerCase();
  if (['piechart', 'pie', 'pie_chart'].includes(t)) return true;
  if (t === 'chart' && (w.widgetName || '').toLowerCase().includes('pie')) return true;
  return false;
};

const GRID_COLS = 12;

/**
 * Reject DB/localStorage layouts that are too small or invalid (they caused tiny tiles on the left).
 */
const sanitizeStoredLayout = (x, y, w, h, minW, minH) => {
  const nx = Number(x);
  const ny = Number(y);
  const nw = Number(w);
  const nh = Number(h);
  if (![nx, ny, nw, nh].every((n) => Number.isFinite(n))) return null;
  if (nx < 0 || ny < 0 || nw < minW || nh < minH) return null;
  if (nw > GRID_COLS || nh > 24) return null;
  if (nx + nw > GRID_COLS) return null;
  return { x: nx, y: ny, w: nw, h: nh };
};

/** Preferred / max tile widths per visual kind (12-col grid). */
const VISUAL_TILE = {
  pie: { prefW: 4, maxW: 6, minW: 3 },
  chart: { prefW: 6, maxW: 12, minW: 4 },
};

/**
 * Split n items into rows as evenly as possible, at most maxPerRow per row.
 * e.g. n=5, maxPerRow=4 → [3, 2] (never a lonely leftover after a full row).
 */
const balancedRows = (n, maxPerRow) => {
  if (n <= 0) return [];
  const rowCount = Math.ceil(n / maxPerRow);
  const base = Math.floor(n / rowCount);
  const extra = n % rowCount;
  return Array.from({ length: rowCount }, (_, r) => base + (r < extra ? 1 : 0));
};

/**
 * Lay a band of same-size items into balanced rows that span the full grid
 * width. Rows whose cards would exceed maxW are capped and centered instead
 * of leaving a gap on the right.
 * Returns the y after the band.
 */
const packEvenBand = (items, { maxPerRow, h, minW, minH, maxW = GRID_COLS }, slots, startY) => {
  let y = startY;
  let idx = 0;
  for (const count of balancedRows(items.length, maxPerRow)) {
    let w = Math.floor(GRID_COLS / count);
    let offset = 0;
    let rem = GRID_COLS - w * count;
    if (w > maxW) {
      w = maxW;
      rem = 0;
      offset = Math.floor((GRID_COLS - count * w) / 2);
    }
    let x = offset;
    for (let c = 0; c < count; c += 1, idx += 1) {
      const ww = w + (c < rem ? 1 : 0);
      slots[items[idx].k] = { x, y, w: ww, h, minW, minH };
      x += ww;
    }
    y += h;
  }
  return y;
};

/**
 * Lay pies and bar/line/map charts together: fill each row greedily by
 * preferred width, then stretch the row's cards (widest-capacity first, so
 * bars absorb slack before pies) until the row spans all 12 columns.
 * A row that cannot stretch to full width (e.g. a single pie) is centered.
 * Returns the y after the band.
 */
const packVisualBand = (items, slots, startY) => {
  const h = 2;
  const minH = 2;
  let y = startY;
  let row = [];
  let used = 0;

  const flushRow = () => {
    if (!row.length) return;
    let leftover = GRID_COLS - used;
    const growOrder = [...row].sort((a, b) => b.maxW - a.maxW);
    for (const it of growOrder) {
      if (leftover <= 0) break;
      const room = Math.min(leftover, it.maxW - it.w);
      it.w += room;
      leftover -= room;
    }
    let x = Math.floor(leftover / 2);
    for (const it of row) {
      slots[it.k] = { x, y, w: it.w, h, minW: it.minW, minH };
      x += it.w;
    }
    y += h;
    row = [];
    used = 0;
  };

  for (const it of items) {
    if (used + it.prefW > GRID_COLS) flushRow();
    row.push({ ...it, w: it.prefW });
    used += it.prefW;
  }
  flushRow();
  return y;
};

/**
 * Default grid slots: KPI strip → visualisations (pies + charts mixed) → AI cards.
 * Every row spans the full 12 columns so no dead space is left on the right.
 * rowHeight default ~150 px: h=1 ≈ 150 px, h=2 ≈ 306 px.
 */
const buildDefaultSlots = (widgets) => {
  const kpis = [];
  const visuals = [];
  const ais = [];

  widgets.forEach((w, i) => {
    const cat = wCategory(w);
    if (cat === 'kpi') kpis.push({ k: wKey(w, i) });
    else if (cat === 'ai') ais.push({ k: wKey(w, i) });
    else visuals.push({ k: wKey(w, i), ...(isPieWidget(w) ? VISUAL_TILE.pie : VISUAL_TILE.chart) });
  });

  const slots = {};
  let y = 0;
  y = packEvenBand(kpis, { maxPerRow: 4, h: 1, minW: 2, minH: 1, maxW: 6 }, slots, y);
  y = packVisualBand(visuals, slots, y);
  packEvenBand(ais, { maxPerRow: 2, h: 2, minW: 3, minH: 2 }, slots, y);
  return slots;
};

/**
 * Build the react-grid-layout layout array for `displayWidgets`.
 *
 * Priority order for each widget's position/size:
 *   1. DB columns (PositionX / PositionY / width / height) from the master
 *      widget list (`sourceWidgets`).  These are the authoritative values.
 *   2. localStorage cache – used when the user navigated away before the
 *      debounced DB write finished.
 *   3. Computed default slot (row-based algorithm above).
 *
 * `sourceWidgets` is the raw dashboardData.widgets array that always contains
 * the full widget metadata including DB-persisted position columns.
 */
const buildLayout = (displayWidgets, sourceWidgets, storedLayout) => {
  const slots = buildDefaultSlots(displayWidgets);

  // Index source widgets by id for fast lookup (handles both string/number ids)
  const sourceById = {};
  (sourceWidgets || []).forEach((w) => {
    if (w.widgetId != null) sourceById[String(w.widgetId)] = w;
  });

  // Index localStorage cache by key
  const storedMap = {};
  (storedLayout || []).forEach((s) => { storedMap[s.i] = s; });

  return displayWidgets.map((w, i) => {
    const key = wKey(w, i);
    const d = slots[key] || { x: 0, y: 0, w: 6, h: 6, minW: 3, minH: 3 };

    // 1 – DB values (PascalCase as stored in DB columns, camelCase as fallback)
    const src = sourceById[String(w.widgetId)] || w;
    const dbX = src.PositionX ?? src.positionX;
    const dbY = src.PositionY ?? src.positionY;
    const dbW = src.width ?? src.Width;
    const dbH = src.height ?? src.Height;
    const dbSan = sanitizeStoredLayout(dbX, dbY, dbW, dbH, d.minW, d.minH);
    if (dbSan) {
      return { i: key, ...dbSan, minW: d.minW, minH: d.minH };
    }

    // 2 – localStorage cache (ignore corrupt / legacy tiny layouts)
    const ls = storedMap[key];
    if (ls) {
      const lsSan = sanitizeStoredLayout(ls.x, ls.y, ls.w, ls.h, d.minW, d.minH);
      if (lsSan) {
        return { i: key, ...lsSan, minW: d.minW, minH: d.minH };
      }
    }

    // 3 – default slot
    return { i: key, ...d };
  });
};

/* ─── Component ─────────────────────────────────────────────────────────────── */

const DashboardPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useTenant();
  const tenantDefaultAiModel = useTenantDefaultAiModel();
  const myOnlyDashboardAccess = isMyDashboardsOnlyAccess(hasPermission);
  const api = useApi();
  const { updateCurrentStep, saveProjectProgress, markUnsavedChanges, trackChange, clearChangesTracker, registerSaveBeforeLeave, unregisterSaveBeforeLeave, currentProject, initializeProject } = useProjectProgress();
  const { showAlert } = useCustomAlert();
  const { showErrorToast } = useErrorToast();

  /* ── Data state ── */
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Draft values in the filter popover (edit until Apply). */
  const [selectedFilters, setSelectedFilters] = useState({});
  /** Last filters committed by Apply (or loaded from server); drives widget source and badge. */
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filteredWidgets, setFilteredWidgets] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  /** widgetId -> true while re-running AI after output mode change */
  const [aiModeExecuting, setAiModeExecuting] = useState({});
  const [dashboardName, setDashboardName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempDashboardName, setTempDashboardName] = useState('');
  const [dashboardNameExists, setDashboardNameExists] = useState(false);
  const [isCheckingDashboardName, setIsCheckingDashboardName] = useState(false);
  const checkDashboardNameTimerRef = useRef(null);
  const [noDataDialogOpen, setNoDataDialogOpen] = useState(false);
  const [noDataDialogMessage, setNoDataDialogMessage] = useState('');
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfExportError, setPdfExportError] = useState(null);
  const widgetsPdfRootRef = useRef(null);
  const [datePickerPopover, setDatePickerPopover] = useState(null);
  const [datePickerEndpoint, setDatePickerEndpoint] = useState('from');
  
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncResponses = async () => {
    if (!currentProject || !currentProject.projectId) return;
    setIsSyncing(true);
    try {
      const response = await api.post('/jobs/refresh-survey', {
        projectId: currentProject.projectId
      });
      if (response.status === 200 || response.status === 201) {
        const aiCards = (dashboardData?.widgets || []).filter(w => isDashboardAiWidgetType(w));

        if (aiCards.length > 0) {
          startProgress('Re-running AI cards with new responses...');
          
          const executePromises = aiCards.map(card => {
            const requestData = {
              jobId: currentProject.projectId,
              widgetType: card.widgetType,
              selectedColumns: card.selectedColumns || [],
              widgetName: card.widgetName,
              widgetId: card.widgetId
            };
            if (card.prompt) {
              requestData.customPrompt = card.prompt;
            }
            if (card.model) requestData.model = card.model;
            if (card.promptType) requestData.promptType = card.promptType;

            return api.post("/widgets/execute", requestData).catch(err => {
              console.error(`Failed to execute AI for card ${card.widgetId}:`, err);
            });
          });

          await Promise.all(executePromises);
          await finishProgress('AI cards updated');
        }

        showAlert('Survey responses synced successfully!', { severity: 'success' });
        // Refresh dashboard data
        fetchDashboardData();
      } else {
        throw new Error(response.data?.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Failed to sync responses:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to sync responses.'));
      stopProgress();
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    if (pdfExportError) {
      showErrorToast(pdfExportError);
      setPdfExportError(null);
    }
  }, [pdfExportError, showErrorToast]);

  /* ── Layout / grid / filter popover ── */
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [gridLayout, setGridLayout] = useState([]);
  const prevLayoutRef = useRef([]);
  const saveTimerRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null);

  /* ── Progress bar — two-phase curve: fast to ~70%, then slow crawl to ~99% ── */
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const progressIntervalRef = useRef(null);
  const progressStartRef = useRef(0);
  const progressFloorRef = useRef(0);

  const stopProgress = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgress = useCallback((message = 'Preparing your dashboard…') => {
    stopProgress();
    progressStartRef.current = Date.now();
    progressFloorRef.current = 0;
    setProgress(0);
    setProgressMessage(message);
    setShowProgress(true);
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      // Two-phase: fast rise to ~70% (τ = 2s), then slow crawl toward ~99% (τ = 18s).
      // The bar never fully stops — it always inches forward until finishProgress() fires.
      const fast = 70 * (1 - Math.exp(-elapsed / 2000));
      const slow = 29 * (1 - Math.exp(-elapsed / 18000));
      const eased = fast + slow;
      const next = Math.max(progressFloorRef.current, eased);
      setProgress(Math.min(next, 99));
    }, 80);
  }, [stopProgress]);

  const setProgressStep = useCallback((message) => {
    if (message) setProgressMessage(message);
  }, []);

  const finishProgress = useCallback((message = 'Dashboard ready') => {
    stopProgress();
    setProgressMessage(message);
    return new Promise((resolve) => {
      setProgress(100);
      // Let the CSS transition (450ms) visually fill the bar, then hide
      setTimeout(() => {
        setShowProgress(false);
        setProgressMessage('');
        resolve();
      }, 550);
    });
  }, [stopProgress]);

  const advanceProgressTarget = useCallback((newTarget) => {
    progressFloorRef.current = Math.max(progressFloorRef.current, Math.min(Math.max(0, newTarget), 99));
  }, []);

  useEffect(() => () => stopProgress(), [stopProgress]);

  const { containerRef: gridContainerRef, width: gridWidth } = useContainerWidth();
  const gridLayoutWidth = useMemo(() => {
    if (gridWidth > 0) return gridWidth;
    if (typeof window === 'undefined') return MIN_GRID_WIDTH;
    return Math.max(280, window.innerWidth - 120);
  }, [gridWidth]);

  /** Folder tree for breadcrumb path (Dashboards explorer). */
  const [foldersFlat, setFoldersFlat] = useState([]);
  /** Resolved folder id for breadcrumb: query `folderId`, else first folder from API. */
  const [breadcrumbFolderId, setBreadcrumbFolderId] = useState(null);

  const folderById = useMemo(() => {
    const map = new Map();
    foldersFlat.forEach((f) => map.set(f.FolderId, f));
    return map;
  }, [foldersFlat]);

  const folderBreadcrumbSegments = useMemo(() => {
    const scopeIsMy = myOnlyDashboardAccess || searchParams.get('scope') === 'my';
    const rootLabel = scopeIsMy ? 'My Dashboards' : 'All Dashboards';
    const base = [{ id: null, label: rootLabel }];
    const fid =
      breadcrumbFolderId && folderById.size && folderById.has(breadcrumbFolderId) ? breadcrumbFolderId : null;
    if (!fid) return base;
    const chain = [];
    const seen = new Set();
    let current = folderById.get(fid);
    while (current && !seen.has(current.FolderId)) {
      chain.push({ id: current.FolderId, label: current.FolderName || 'Folder' });
      seen.add(current.FolderId);
      current = current.ParentFolderId ? folderById.get(current.ParentFolderId) : null;
    }
    chain.reverse();
    return [...base, ...chain];
  }, [breadcrumbFolderId, folderById, searchParams, myOnlyDashboardAccess]);

  const loadFoldersForBreadcrumb = useCallback(async () => {
    try {
      const res = await api.get('/folder');
      const raw = res.data?.data;
      const list = Array.isArray(raw) ? raw : [];
      const tree = buildFolderTree(list);
      setFoldersFlat(flattenFolders(tree, []));
    } catch (e) {
      console.warn('Could not load folders for breadcrumb:', e);
      setFoldersFlat([]);
    }
  }, [api]);

  useEffect(() => {
    loadFoldersForBreadcrumb();
  }, [loadFoldersForBreadcrumb]);

  useEffect(() => {
    const fid = searchParams.get('folderId');
    setBreadcrumbFolderId(fid && String(fid).trim() !== '' ? String(fid).trim() : null);
  }, [searchParams]);

  useEffect(() => {
    updateCurrentStep('dashboard');
  }, [updateCurrentStep]);

  useEffect(() => {
    registerSaveBeforeLeave(async () => {
      if (isEditingName && tempDashboardName.trim() && tempDashboardName.trim() !== dashboardName) {
        const ok = await handleSaveName();
        if (!ok) return { success: false };
      }
      if (projectId && appliedFilters && Object.keys(appliedFilters).length >= 0) {
        try { await api.post(`/dashboards/${projectId}`, { filters: appliedFilters, model: tenantDefaultAiModel }); }
        catch (e) { console.warn('Could not save filter selections:', e); }
      }
      return { success: true };
    });
    return () => unregisterSaveBeforeLeave();
  }, [registerSaveBeforeLeave, unregisterSaveBeforeLeave, isEditingName, tempDashboardName, dashboardName, projectId, appliedFilters]);

  /* ─── Data fetching ─────────────────────────────────────────────────────── */

  const fetchDashboardData = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    startProgress('Loading dashboard configuration…');
    try {
      let data;
      try {
        const response = await api.get(`/dashboards/${projectId}`);
        const res = response.data?.data ?? response.data;
        setProgressStep('Preparing widgets and filters…');
        if (res && (res.widgets || res.filters !== undefined)) {
          data = res;
          if (!data.filters && Array.isArray(data.widgets)) {
            setProgressStep('Loading filters…');
            const filterRes = await api.get(`/filters/${projectId}`);
            const filterList = filterRes.data?.data ?? filterRes.data;
            data.filters = Array.isArray(filterList) ? filterList : [];
          }
        } else { data = null; }
      } catch (e) {
        if (e.response?.status !== 404) throw e;
        data = null;
      }

      if (!data) {
        setProgressStep('Fetching widgets and filters…');
        const [widgetsRes, filtersRes] = await Promise.all([
          api.get(`/widgets/${projectId}`),
          api.get(`/filters/${projectId}`),
        ]);
        setProgressStep('Processing widget data…');
        const widgetsList = widgetsRes.data?.data ?? widgetsRes.data;
        const filtersList = filtersRes.data?.data ?? filtersRes.data;
        const widgetsArray = Array.isArray(widgetsList) ? widgetsList : [];
        const filtersArray = Array.isArray(filtersList) ? filtersList : [];
        data = {
          widgets: widgetsArray.map((w) => ({
            widgetId: w.widgetId,
            projectId: w.projectId,
            widgetName: w.widgetName ?? w.widgetTitle,
            widgetType: w.widgetType ?? 'barchart',
            selectedColumns: w.selectedColumns || [],
            aggregation: w.aggregation ?? null,
            query: w.query ?? '',
            data: null,
            hasResults: false,
            widgetResults: null,
            sourceColumns: Array.isArray(w.selectedColumns)
              ? w.selectedColumns.join(',')
              : (w.sourceColumns || ''),
          })),
          filters: filtersArray,
          dashboardName: 'Dashboard for project',
        };
      }

      // Always initialize project to prevent state leakage between dashboards (e.g. leaking SourceSurveyId)
      initializeProject(
        {
          ...currentProject,
          ...data,
          projectId: projectId,
          projectName: data.projectName || data.dashboardName,
          SourceSurveyId: data.SourceSurveyId || data.sourceSurveyId || null,
          currentStep: 'dashboard'
        },
        { markUnsaved: false, markAsSaved: true }
      );

      setProgressStep('Building dashboard layout…');
      setDashboardData(data);
      setDashboardName(data.dashboardName || data.DashboardName || 'Dashboard for project');
      if (data.filters?.length > 0) {
        data.filters.forEach((f) => {
          if (f.availableValues === undefined && f.columnName) f.availableValues = [];
        });
      }

      const saved = data.selectedFilters && typeof data.selectedFilters === 'object'
        ? data.selectedFilters : {};
      const savedClone = cloneDashboardFilters(saved);
      setSelectedFilters(savedClone);
      setAppliedFilters(savedClone);
      setFilteredWidgets([]);

      if (Object.keys(saved).length > 0) {
        setProgressStep('Applying saved filters…');
        setIsFiltering(true);
        try {
          const applyRes = await api.post(`/dashboards/${projectId}`, { filters: saved, model: tenantDefaultAiModel });
          const applyData = applyRes.data?.data ?? applyRes.data;
          setProgressStep('Refreshing widgets with filters…');
          if (applyData?.noDataForFilters) {
            setNoDataDialogMessage(applyData.message || 'No data for the selected filters.');
            setNoDataDialogOpen(true);
          } else {
            setFilteredWidgets(applyData?.widgets || []);
          }
        } catch (e) {
          console.warn('Could not apply saved filters on load:', e);
        } finally {
          setIsFiltering(false);
        }
      }

      setProgressStep('Finalizing dashboard…');
      await finishProgress('Dashboard ready');
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(getApiErrorMessage(err, 'Failed to load dashboard data.'));
      await finishProgress('Could not load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) fetchDashboardData(); }, [projectId]);

  /* ─── Widget list (ordered KPI → visual → AI) ──────────────────────────── */

  const getWidgetOutputMode = useCallback((widget) => {
    if (widget?.outputMode) return widget.outputMode;
    return parseAiWidgetConfig(widget?.query).outputMode || DEFAULT_OUTPUT_MODE;
  }, []);

  const getWidgetPromptType = useCallback((widget) => {
    if (widget?.promptType) return widget.promptType;
    return parseAiWidgetConfig(widget?.query).promptType || DEFAULT_PROMPT_TYPE;
  }, []);

  const patchWidgetInDashboardSources = useCallback((widgetId, patchFn) => {
    const apply = (list) =>
      Array.isArray(list)
        ? list.map((w) => (String(w.widgetId) === String(widgetId) ? patchFn({ ...w }) : w))
        : list;
    setDashboardData((prev) =>
      prev?.widgets ? { ...prev, widgets: apply(prev.widgets) } : prev
    );
    setFilteredWidgets((prev) => apply(prev));
  }, []);

  const handleAiOutputModeChange = useCallback(
    async (widget, newMode) => {
      const widgetId = widget?.widgetId;
      if (!widgetId || !projectId || newMode === getWidgetOutputMode(widget)) return;

      setAiModeExecuting((prev) => ({ ...prev, [widgetId]: true }));
      try {
        const promptType = getWidgetPromptType(widget);
        const executeBody = {
          jobId: projectId,
          widgetId,
          widgetType: widget.widgetType,
          widgetName: widget.widgetName,
          selectedColumns: widget.selectedColumns || [],
          model: tenantDefaultAiModel,
          promptType,
          outputMode: newMode,
        };
        if (hasActiveDashboardFilters(appliedFilters)) {
          executeBody.filters = appliedFilters;
        }
        const wt = (widget.widgetType || '').toLowerCase();
        if (wt === 'custom_ai_card' && (widget.prompt || '').trim()) {
          executeBody.customPrompt = widget.prompt.trim();
        }

        const execRes = await api.post('/widgets/execute', executeBody);
        const payload = execRes.data?.data ?? execRes.data;
        if (payload?.error) {
          showErrorToast(payload.error || 'Could not regenerate insights for this style.');
          return;
        }

        const mergedQuery = mergeAiConfigIntoQuery(widget.query, { outputMode: newMode, promptType });
        try {
          await api.put(`/widgets/${widgetId}`, {
            widgetTitle: widget.widgetName,
            widgetName: widget.widgetName,
            widgetType: widget.widgetType,
            selectedColumns: widget.selectedColumns || [],
            prompt: widget.prompt || '',
            query: mergedQuery,
            widgetResults: payload,
            tags: payload?.tags,
          });
        } catch (saveErr) {
          console.warn('AI results saved in session; widget config persist failed:', saveErr);
        }

        patchWidgetInDashboardSources(widgetId, (w) => ({
          ...w,
          outputMode: newMode,
          promptType,
          query: mergedQuery,
          hasResults: true,
          widgetResults: { results: payload },
        }));
      } catch (err) {
        console.error('Output mode re-execute failed:', err);
        showErrorToast(
          getApiErrorMessage(err, 'Could not regenerate insights. Try again or pick another output style.')
        );
      } finally {
        setAiModeExecuting((prev) => {
          const next = { ...prev };
          delete next[widgetId];
          return next;
        });
      }
    },
    [
      projectId,
      api,
      tenantDefaultAiModel,
      appliedFilters,
      getWidgetOutputMode,
      getWidgetPromptType,
      patchWidgetInDashboardSources,
      showErrorToast,
    ]
  );

  const filtersActive = hasActiveDashboardFilters(appliedFilters);
  const widgetsSource = useMemo(
    () =>
      filtersActive && filteredWidgets.length > 0
        ? filteredWidgets
        : (dashboardData?.widgets || []),
    [filtersActive, filteredWidgets, dashboardData?.widgets]
  );
  const kpiWidgets = widgetsSource.filter(
    (w) => !isDashboardAiWidgetType(w) && !w.hasResults && w.widgetType === 'kpi'
  );
  const visualWidgets = widgetsSource.filter(
    (w) =>
      !isDashboardAiWidgetType(w) &&
      !w.hasResults &&
      ['barchart', 'linechart', 'piechart', 'chart', 'line_chart', 'line', 'pie_chart', 'bar_chart', 'bar', 'pie', 'map'].includes(w.widgetType)
  );
  const aiWidgets = widgetsSource.filter((w) => w.hasResults || isDashboardAiWidgetType(w));
  const allWidgets = [...kpiWidgets, ...visualWidgets, ...aiWidgets];

  const progressPercent = Math.round(progress);

  /* ─── Grid layout initialisation ───────────────────────────────────────── */

  useEffect(() => {
    setGridLayout([]);
    prevLayoutRef.current = [];
  }, [projectId]);

  const widgetLayoutSig = allWidgets.map((w, i) => wKey(w, i)).join('|');

  const builtLayout = useMemo(() => {
    if (!allWidgets.length || !projectId) return [];
    let storedLayout = null;
    try {
      const raw = localStorage.getItem(LAYOUT_KEY(projectId));
      if (raw) storedLayout = JSON.parse(raw);
    } catch { /* ignore */ }
    return buildLayout(allWidgets, dashboardData?.widgets, storedLayout);
    // widgetLayoutSig: stable when widget ids/order unchanged (allWidgets is a new array each render)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allWidgets aligned with widgetLayoutSig
  }, [widgetLayoutSig, projectId, dashboardData?.widgets]);

  /**
   * If React state still holds a previous project's or filtered subset's layout, keys won't match
   * current children. react-grid-layout then assigns every unmatched item w:1 h:1 (tiny stack on the left).
   */
  const effectiveGridLayout = useMemo(() => {
    if (!builtLayout.length) return gridLayout;
    if (!layoutKeysAlign(gridLayout, allWidgets)) return builtLayout;
    return gridLayout;
  }, [gridLayout, builtLayout, allWidgets]);

  useLayoutEffect(() => {
    if (!projectId || !builtLayout.length) return;
    if (!layoutKeysAlign(gridLayout, allWidgets)) {
      setGridLayout(builtLayout);
      prevLayoutRef.current = builtLayout;
    }
  }, [projectId, builtLayout, allWidgets, gridLayout]);

  /**
   * Called after every drag-stop or resize-stop.
   * 1. Updates React state immediately so the grid re-renders.
   * 2. Writes the full layout to localStorage right away (instant cache).
   * 3. Diffs against the previous layout to find changed widgets, then
   *    debounces a PATCH /widgets/{id} call for each one (600 ms).
   *    Only widgets whose x / y / w / h actually changed are sent to the DB.
   */
  const persistLayout = useCallback((newLayout) => {
    setGridLayout(newLayout);

    // Instant localStorage write (survives rapid navigation before DB write)
    try { localStorage.setItem(LAYOUT_KEY(projectId), JSON.stringify(newLayout)); } catch { /* quota */ }

    // Diff: only PATCh widgets whose position/size changed
    const prevMap = Object.fromEntries(prevLayoutRef.current.map((item) => [item.i, item]));
    const changed = newLayout.filter((item) => {
      const prev = prevMap[item.i];
      return !prev || prev.x !== item.x || prev.y !== item.y || prev.w !== item.w || prev.h !== item.h;
    });
    prevLayoutRef.current = newLayout;

    if (changed.length === 0) return;

    // Debounce the DB write – if the user keeps resizing, restart the timer
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');

    saveTimerRef.current = setTimeout(async () => {
      try {
        await Promise.all(
          changed.map((item) =>
            api.patch(`/widgets/${item.i}`, {
              PositionX: item.x,
              PositionY: item.y,
              width: item.w,
              height: item.h,
            }).catch((e) => {
              // Non-fatal: log but don't break other saves
              console.warn(`Layout save failed for widget ${item.i}:`, e.message);
            })
          )
        );
        setSaveStatus('saved');
        // Clear the "saved" badge after 2 s
        setTimeout(() => setSaveStatus(null), 2000);
      } catch (e) {
        console.error('Layout DB save error:', e);
        setSaveStatus('error');
      }
    }, 600);
  }, [api, projectId]);

  const handleLayoutChange = useCallback((layout) => {
    setGridLayout(layout);
  }, []);

  const handleDragStop = useCallback((_layout) => {
    persistLayout(_layout);
  }, [persistLayout]);

  const handleResizeStop = useCallback((_layout) => {
    persistLayout(_layout);
  }, [persistLayout]);

  /**
   * Recompute the optimal default layout for all current widgets and persist
   * it (localStorage + DB), replacing any manual arrangement.
   */
  const handleAutoArrange = () => {
    if (!allWidgets.length) return;
    const slots = buildDefaultSlots(allWidgets);
    const arranged = allWidgets.map((w, i) => {
      const key = wKey(w, i);
      const d = slots[key] || { x: 0, y: 0, w: 6, h: 2, minW: 3, minH: 2 };
      return { i: key, ...d };
    });
    persistLayout(arranged);
  };

  const openFilterMenu = (event) => {
    setSelectedFilters(cloneDashboardFilters(appliedFilters));
    setFilterAnchorEl(event.currentTarget);
  };
  /** Closing without Apply discards draft so it matches what is on the dashboard. */
  const closeFilterMenu = () => {
    setFilterAnchorEl(null);
    setSelectedFilters(cloneDashboardFilters(appliedFilters));
  };

  /* ─── Filter handlers ───────────────────────────────────────────────────── */

  const handleFilterChange = (columnName, value) =>
    setSelectedFilters((prev) => ({ ...prev, [columnName]: value }));

  const handleMultiSelectChange = (columnName, values) =>
    setSelectedFilters((prev) => ({ ...prev, [columnName]: values }));

  const handleDateRangeChange = (columnName, dateRange) =>
    setSelectedFilters((prev) => ({ ...prev, [columnName]: dateRange }));

  const handleClearFilter = (columnName) => {
    const filter = dashboardData?.filters?.find((f) => f.columnName === columnName);
    let clearValue = '';
    if (filter) {
      const t = (filter.filterType || '').toLowerCase();
      if (['radio', 'radiobutton', 'checkbox', 'multiselect'].some((x) => t.includes(x)))
        clearValue = [];
      else if (isDatePickerFilterType(filter.filterType || filter.FilterType))
        clearValue = { from: '', to: '' };
      else clearValue = filter.isMultiSelect ? [] : '';
    }
    setSelectedFilters((prev) => ({ ...prev, [columnName]: clearValue }));
  };

  const applyFilters = async () => {
    try {
      setIsFiltering(true);
      setError(null);
      startProgress('Applying filters…');
      const response = await api.post(`/dashboards/${projectId}`, { filters: { ...selectedFilters }, model: tenantDefaultAiModel });
      setProgressStep('Updating widgets with new filters…');
      const res = response.data?.data ?? response.data;
      if (res?.noDataForFilters) {
        setSelectedFilters(cloneDashboardFilters(appliedFilters));
        setNoDataDialogMessage(res.message || 'No data for the selected filters.');
        setNoDataDialogOpen(true);
        await finishProgress('No data for selected filters');
        return;
      }
      const committed = cloneDashboardFilters(selectedFilters);
      setAppliedFilters(committed);
      const stillActive = hasActiveDashboardFilters(committed);
      const newWidgets = stillActive ? (res?.widgets || []) : [];
      setFilteredWidgets(newWidgets);
      setFilterAnchorEl(null);
      setProgressStep('Finalizing…');
      await finishProgress('Filters applied');
    } catch (err) {
      console.error('Error applying filters:', err);
      setError(getApiErrorMessage(err, 'Failed to apply filters.'));
      await finishProgress('Could not apply filters');
    } finally {
      setIsFiltering(false);
    }
  };

  const checkDashboardName = useCallback((name) => {
    if (checkDashboardNameTimerRef.current) clearTimeout(checkDashboardNameTimerRef.current);

    const trimmed = name.trim();
    if (!trimmed || trimmed === dashboardName) {
      setDashboardNameExists(false);
      setIsCheckingDashboardName(false);
      return;
    }

    setIsCheckingDashboardName(true);
    checkDashboardNameTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/dashboards/check-name?name=${encodeURIComponent(trimmed)}`);
        setDashboardNameExists(response.data?.data?.exists === true);
      } catch (err) {
        console.error('Error checking dashboard name:', err);
        setDashboardNameExists(false);
      } finally {
        setIsCheckingDashboardName(false);
      }
    }, 400);
  }, [api, dashboardName]);

  useEffect(() => {
    return () => {
      if (checkDashboardNameTimerRef.current) clearTimeout(checkDashboardNameTimerRef.current);
    };
  }, []);

  const handleEditName = () => {
    setTempDashboardName(dashboardName);
    setDashboardNameExists(false);
    setIsCheckingDashboardName(false);
    setIsEditingName(true);
    trackChange(CHANGE_TYPES.EDIT_DASHBOARD_NAME, 'Edit dashboard name');
  };

  const handleTempDashboardNameChange = (e) => {
    const val = e.target.value;
    setTempDashboardName(val);
    checkDashboardName(val);
  };

  const handleSaveName = async () => {
    if (!tempDashboardName.trim()) {
      showAlert('Dashboard name cannot be empty', { severity: 'warning' });
      return false;
    }
    if (dashboardNameExists) return;
    try {
      await api.put(`/dashboards/${projectId}`, {
        dashboardName: tempDashboardName.trim()
      });
      setDashboardName(tempDashboardName.trim());
      setIsEditingName(false);
      clearChangesTracker();
      showAlert('Dashboard name updated successfully!', { severity: 'success' });
      return true;
    } catch (err) {
      console.error('Error updating dashboard name:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to update dashboard name.'));
      return false;
    }
  };

  const handleCancelEdit = () => {
    setTempDashboardName('');
    setDashboardNameExists(false);
    setIsCheckingDashboardName(false);
    setIsEditingName(false);
  };

  const handleEditDashboard = () => {
    navigate(`/cards/${projectId}`);
  };

  const handleDownloadPdf = useCallback(async () => {
    setPdfExportError(null);
    const root = widgetsPdfRootRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll('[data-dashboard-pdf-widget]'));
    if (nodes.length === 0) return;

    // Sort nodes based on effectiveGridLayout (y, then x)
    const layoutMap = new Map((effectiveGridLayout || []).map((item) => [item.i, item]));
    nodes.sort((a, b) => {
      const keyA = a.getAttribute('data-widget-key');
      const keyB = b.getAttribute('data-widget-key');
      const layoutA = layoutMap.get(keyA);
      const layoutB = layoutMap.get(keyB);

      if (!layoutA || !layoutB) return 0;

      if (layoutA.y !== layoutB.y) {
        return layoutA.y - layoutB.y; // Top to bottom
      }
      return layoutA.x - layoutB.x; // Left to right
    });

    setPdfExporting(true);
    try {
      await exportWidgetsToPdf(nodes, { fileNameBase: dashboardName || 'dashboard' });
    } catch (e) {
      console.error(e);
      setPdfExportError(getApiErrorMessage(e, 'Could not generate PDF. Try again.'));
    } finally {
      setPdfExporting(false);
    }
  }, [dashboardName, effectiveGridLayout]);

  const hasFilterValue = (columnName) => {
    const v = selectedFilters[columnName];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object' && (v.from || v.to || v.fromDate || v.toDate)) return true;
    return String(v).trim() !== '';
  };

  /* ─── Filter renderer ───────────────────────────────────────────────────── */

  const renderFilter = (filter) => {
    const { filterType, filterId, columnName, filterName, availableValues = [], isMultiSelect } = filter;
    const formattedName = formatBusinessText(filterName);
    const multi = isMultiSelect === true || isMultiSelect === 1 || isMultiSelect === '1';
    const showRemove = hasFilterValue(columnName);

    if (!isDatePickerFilterType(filterType) && (!availableValues || availableValues.length === 0)) return null;

    const removeBtn = showRemove ? (
      <Tooltip title="Clear filter">
        <IconButton size="small" onClick={() => handleClearFilter(columnName)} sx={{ p: 0.25, color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    ) : null;

    switch (isDatePickerFilterType(filterType) ? 'datepicker' : (filterType || '').toLowerCase()) {
      case 'datepicker': {
        const fromVal = selectedFilters[columnName]?.from || '';
        const toVal = selectedFilters[columnName]?.to || '';
        const rangeSummary = fromVal && toVal ? `${fromVal} → ${toVal}` : fromVal ? `From ${fromVal}` : toVal ? `To ${toVal}` : '';
        const popoverOpen = Boolean(datePickerPopover?.columnName === columnName && datePickerPopover?.anchorEl);
        return (
          <Box key={filterId} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
              <Button
                variant="text" size="small"
                onClick={(e) => { setDatePickerEndpoint('from'); setDatePickerPopover({ anchorEl: e.currentTarget, columnName }); }}
                endIcon={<CalendarMonthIcon sx={{ fontSize: 16, color: ACCENT }} />}
                sx={{ textTransform: 'none', fontFamily: FONT_FAMILY_PRIMARY, color: '#000', justifyContent: 'flex-start', flex: 1, minWidth: 0, px: 0.5, fontWeight: 600, '& .MuiButton-endIcon': { ml: 0.5 } }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography component="span" variant="subtitle2" sx={{ fontFamily: FONT_FAMILY_PRIMARY, lineHeight: 1.2, fontSize: '11px' }}>{formattedName}</Typography>
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '10px' }}>
                    {rangeSummary || 'Choose dates'}
                  </Typography>
                </Box>
              </Button>
              {removeBtn}
            </Box>
            <Popover open={popoverOpen} anchorEl={datePickerPopover?.anchorEl} onClose={() => setDatePickerPopover(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.08)' } }}
            >
              <Box sx={{ p: 2, minWidth: 280 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 700 }}>{formattedName}</Typography>
                <ToggleButtonGroup exclusive fullWidth size="small" value={datePickerEndpoint}
                  onChange={(_, v) => { if (v !== null) setDatePickerEndpoint(v); }}
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="from" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>From</ToggleButton>
                  <ToggleButton value="to" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>To</ToggleButton>
                </ToggleButtonGroup>
                <TextField type="date" fullWidth size="small"
                  label={datePickerEndpoint === 'from' ? 'Start date' : 'End date'}
                  value={datePickerEndpoint === 'from' ? fromVal : toVal}
                  onChange={(e) => handleDateRangeChange(columnName, { ...selectedFilters[columnName], [datePickerEndpoint]: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <Button fullWidth variant="contained" size="small" onClick={() => setDatePickerPopover(null)}
                  sx={{ mt: 2, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: 'rgb(50,100,120)' }, borderRadius: 1 }}
                >Done</Button>
              </Box>
            </Popover>
          </Box>
        );
      }
      default:
        if (multi) {
          return (
            <Box key={filterId} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <InputLabel sx={{ fontSize: '11px' }}>{formattedName}</InputLabel>
                <Select multiple value={selectedFilters[columnName] || []}
                  onChange={(e) => handleMultiSelectChange(columnName, e.target.value)}
                  label={formattedName}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" sx={{ height: 18, fontSize: '10px' }} />)}
                    </Box>
                  )}
                  sx={{ fontSize: '12px' }}
                >
                  {availableValues.map((value) => (
                    <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                      <Checkbox checked={(selectedFilters[columnName] || []).indexOf(value) > -1} size="small" />
                      <ListItemText primary={value} primaryTypographyProps={{ fontSize: '12px' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {removeBtn}
            </Box>
          );
        }
        return (
          <Box key={filterId} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
              <InputLabel sx={{ fontSize: '11px' }}>{formattedName}</InputLabel>
              <Select value={selectedFilters[columnName] || ''}
                onChange={(e) => handleFilterChange(columnName, e.target.value)}
                label={formattedName} sx={{ fontSize: '12px' }}
              >
                <MenuItem value="" sx={{ fontSize: '12px' }}><em>All</em></MenuItem>
                {availableValues.map((value) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {removeBtn}
          </Box>
        );
    }
  };

  /* ─── Widget renderers ──────────────────────────────────────────────────── */

  /** Common "no data" placeholder card that fills the grid cell. */
  const NoDataCard = ({ title, message }) => (
    <Card elevation={0} className="dash-card dash-card--empty">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: '14px !important' }}>
        <Typography className="dash-card__title">{title}</Typography>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.4 }}>{message}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const isFilterActive = () => hasActiveDashboardFilters(appliedFilters);

  const renderBarChart = (widget) => {
    const { data, widgetName, sourceColumns, aggregation, selectedColumns, widgetType, query } = widget;
    
    let showValuesOnBars = false;
    let isSeriesGrouped = false;
    try {
      if (query && query.startsWith('{')) {
        const q = JSON.parse(query);
        showValuesOnBars = !!q.showValuesOnBars;
        isSeriesGrouped = q.__groupedBar === 'series';
      }
    } catch (e) {
      // ignore
    }

    if (!data || data.length === 0) {
      return (
        <NoDataCard
          title={widgetName}
          message={isFilterActive() ? 'No data for the selected filters.' : 'No data available.'}
        />
      );
    }

    const stacked = pivotStackedBarLongRows(data);
    if (stacked.isStacked && stacked.chartData?.length > 0 && stacked.segmentKeys?.length > 0) {
      return (
        <Card elevation={0} className="dash-card">
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important', pb: '6px !important' }}>
            <Typography className="dash-card__title" sx={{ flexShrink: 0, mb: 0.5 }}>{widgetName}</Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stacked.chartData} margin={{ top: showValuesOnBars ? 16 : 4, right: 6, left: -22, bottom: 32 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    angle={-35}
                    textAnchor="end"
                    height={48}
                    interval={0}
                    tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }}
                    tickFormatter={(v) => {
                      const s = formatBusinessText(v);
                      return typeof s === 'string' && s.length > 12 ? `${s.substring(0, 10)}…` : s;
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }} width={38} axisLine={false} tickLine={false} />
                  {!showValuesOnBars && (
                    <RechartsTooltip
                      contentStyle={{ fontSize: '12px', fontFamily: FONT_FAMILY_PRIMARY, borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                      cursor={{ fill: 'rgba(64,121,141,0.06)' }}
                    />
                  )}
                  <Legend
                    wrapperStyle={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, paddingTop: 4 }}
                    iconSize={8}
                    formatter={(v) => (typeof v === 'string' && v.length > 14 ? `${v.substring(0, 12)}…` : v)}
                  />
                  {stacked.segmentKeys.map((key, i) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId={isSeriesGrouped ? undefined : 'dashStack'}
                      fill={COLORS[i % COLORS.length]}
                      name={key}
                      maxBarSize={48}
                      isAnimationActive={false}
                      label={showValuesOnBars ? { position: 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const chartData = data.map((item) => ({
      name: extractBarChartCategoryLabel(item),
      value: extractChartNumericValue(item),
    }));

    const t = (widgetType || '').toLowerCase();
    const isBarChartType =
      ['barchart', 'bar', 'bar_chart'].includes(t) ||
      (t === 'chart' && (widgetName || '').toLowerCase().includes('bar'));

    /* Single-value KPI-style card — only for KPI widgets; bar charts always use the chart */
    const isSingleValue =
      !isBarChartType &&
      chartData.length === 1 &&
      aggregation &&
      ['sum', 'count', 'average', 'min', 'max', 'median'].includes(String(aggregation).toLowerCase());

    if (isSingleValue) {
      const value = chartData[0]?.value ?? 0;
      const displayValue = Number.isInteger(value) ? value : value.toFixed(2);
      const colLabel = Array.isArray(selectedColumns) && selectedColumns[0]
        ? selectedColumns[0]
        : String(sourceColumns || '').split(',')[0]?.trim() || '';
      return (
        <Card elevation={0} className="dash-card dash-card--kpi">
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '14px !important', gap: 0.5 }}>
            <Typography className="dash-card__label">{widgetName}</Typography>
            <Typography className="dash-card__kpi-value">{displayValue}</Typography>
            <Typography className="dash-card__kpi-sub">
              {widget.description || `${chartData[0]?.name || aggregation}${colLabel ? ` · ${formatBusinessText(colLabel)}` : ''}`}
            </Typography>
          </CardContent>
        </Card>
      );
    }


    return (
      <Card elevation={0} className="dash-card">
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important', pb: '6px !important' }}>
          <Typography className="dash-card__title" sx={{ flexShrink: 0, mb: 0.5 }}>{widgetName}</Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: showValuesOnBars ? 16 : 4, right: 6, left: -22, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="name" angle={-35} textAnchor="end" height={48} interval={0}
                  tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }}
                  tickFormatter={(v) => (typeof v === 'string' && v.length > 12 ? `${v.substring(0, 10)}…` : v)}
                  axisLine={false} tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }} width={38} axisLine={false} tickLine={false} />
                {!showValuesOnBars && (
                  <RechartsTooltip
                    contentStyle={{ fontSize: '12px', fontFamily: FONT_FAMILY_PRIMARY, borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                    cursor={{ fill: 'rgba(64,121,141,0.06)' }}
                  />
                )}
                <Bar 
                  dataKey="value"
                  fill={COLORS[0]}
                  radius={[3, 3, 0, 0]}
                  name="Value"
                  maxBarSize={48}
                  isAnimationActive={false}
                  label={showValuesOnBars ? { position: 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderLineChart = (widget) => {
    const { data, widgetName } = widget;
    if (!data || data.length === 0) {
      return (
        <NoDataCard
          title={widgetName}
          message={isFilterActive() ? 'No data for the selected filters.' : 'No data available.'}
        />
      );
    }

    const chartData = data
      .map((item) => ({
        name: extractBarChartCategoryLabel(item),
        value: extractChartNumericValue(item),
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));

    return (
      <Card elevation={0} className="dash-card">
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important', pb: '6px !important' }}>
          <Typography className="dash-card__title" sx={{ flexShrink: 0, mb: 0.5 }}>{widgetName}</Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 6, left: -6, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="name"
                  angle={-35}
                  textAnchor="end"
                  height={48}
                  interval={0}
                  tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }}
                  tickFormatter={(v) => (typeof v === 'string' && v.length > 12 ? `${v.substring(0, 10)}…` : v)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, fill: '#555' }} width={38} axisLine={false} tickLine={false} />
                <RechartsTooltip
                  contentStyle={{ fontSize: '12px', fontFamily: FONT_FAMILY_PRIMARY, borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                  cursor={{ stroke: 'rgba(64,121,141,0.35)', strokeWidth: 1 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={COLORS[0]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  name="Value"
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderPieChart = (widget) => {
    const { data, widgetName, query } = widget;
    if (!data || data.length === 0) {
      return (
        <NoDataCard
          title={widgetName}
          message={isFilterActive() ? 'No data for the selected filters.' : 'No data available.'}
        />
      );
    }
    const chartData = data
      .map((item, i) => {
        const name = extractBarChartCategoryLabel(item);
        const value = extractChartNumericValue(item);
        if (isNaN(value) || value <= 0) return null;
        return { name, value, id: `${name}-${i}` };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value);

    if (!chartData.length) return <NoDataCard title={widgetName} message="No valid data" />;

    let showValueType = 'percentage';
    let decimalPoints = 0;
    let customColors = {};
    try {
      if (query && query.startsWith('{')) {
        const q = JSON.parse(query);
        if (q.showValueType) showValueType = q.showValueType;
        if (q.decimalPoints !== undefined) decimalPoints = q.decimalPoints;
        if (q.customColors) customColors = q.customColors;
      }
    } catch (e) {}

    return (
      <Card elevation={0} className="dash-card">
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important', pb: '6px !important' }}>
          <Typography className="dash-card__title" sx={{ flexShrink: 0, mb: 0.5 }}>{widgetName}</Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData} cx="50%" cy="46%" outerRadius="60%" dataKey="value"
                  isAnimationActive={false}
                  labelLine={false}
                  label={({ percent, value }) => {
                    if (percent < 0.05) return '';
                    const displayVal = showValueType === 'percentage' ? percent * 100 : value;
                    const formattedVal = Number(displayVal).toFixed(decimalPoints);
                    return `${formattedVal}${showValueType === 'percentage' ? '%' : ''}`;
                  }}
                >
                  {chartData.map((entry, i) => {
                    const fill = customColors[entry.name] || COLORS[i % COLORS.length];
                    return <Cell key={entry.id} fill={fill} />;
                  })}
                </Pie>
                <RechartsTooltip
                  contentStyle={{ fontSize: '12px', fontFamily: FONT_FAMILY_PRIMARY, borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                />
                <Legend
                  iconSize={10}
                  formatter={(v) => <span style={{ fontSize: 10, fontFamily: FONT_FAMILY_PRIMARY, color: '#555' }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderMap = (widget) => {
    const { data, widgetName } = widget;
    if (!data || data.length === 0) return <NoDataCard title={widgetName} message="No map data available." />;
    return (
      <Card elevation={0} className="dash-card">
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important' }}>
          <Typography className="dash-card__title" sx={{ flexShrink: 0, mb: 0.5 }}>{widgetName}</Typography>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {data.slice(0, 10).map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  {formatBusinessText(item.location || item.name || 'Unknown')}
                </Typography>
                <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 600 }}>
                  {typeof item.total === 'number' ? item.total.toFixed(2) : item.value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderAiCard = (widget) => {
    const { widgetName, widgetType, widgetResults, results, widgetId } = widget;
    let parsed = widgetResults?.results ?? results;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { parsed = null; }
    }
    const modeLoading = Boolean(widgetId && aiModeExecuting[widgetId]);
    if (modeLoading) {
      parsed = null;
    } else if (parsed == null) {
      parsed = {
        error:
          'Insight could not be loaded for this view. Try reloading the dashboard or adjusting filters.',
      };
    }
    const t = (widgetType || '').toLowerCase();
    const rendererType =
      t === 'major_issues' ? 'analysis' : t === 'custom_ai' ? 'custom_ai_card' : widgetType;
    const outputMode = getWidgetOutputMode(widget);
    return (
      <Card elevation={0} className="dash-card dash-card--ai">
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexShrink: 0, width: '100%' }}>
            <AutoFixHighIcon sx={{ color: ACCENT, fontSize: '1rem', flexShrink: 0 }} />
            <Typography className="dash-card__title" sx={{ flex: 1, minWidth: 0 }}>
              {widgetName}
            </Typography>
            <OutputModeSwitcher
              value={outputMode}
              loading={modeLoading}
              disabled={!widgetId || isFiltering}
              onChange={(mode) => handleAiOutputModeChange(widget, mode)}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
            {modeLoading ? (
              <AiExecutionLoader variant="restyle" label="Updating output style…" size={22} />
            ) : (
              <WidgetResultsRenderer results={parsed} widgetType={rendererType} showProcessingInfo={false} />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderWidget = (widget) => {
    const { widgetType, hasResults, widgetName } = widget;
    if (hasResults || isDashboardAiWidgetType(widget)) return renderAiCard(widget);
    if (
      widgetType === 'barchart' || widgetType === 'kpi' || widgetType === 'bar' ||
      widgetType === 'bar_chart' || (widgetType === 'chart' && widgetName?.toLowerCase().includes('bar'))
    ) return renderBarChart(widget);
    if (
      widgetType === 'linechart' || widgetType === 'line' || widgetType === 'line_chart' ||
      (widgetType === 'chart' && widgetName?.toLowerCase().includes('line'))
    ) return renderLineChart(widget);
    if (
      widgetType === 'piechart' || widgetType === 'pie' || widgetType === 'pie_chart' ||
      (widgetType === 'chart' && widgetName?.toLowerCase().includes('pie'))
    ) return renderPieChart(widget);
    if (widgetType === 'map' || widgetName?.toLowerCase().includes('map')) return renderMap(widget);
    return renderBarChart(widget);
  };

  /* ─── Early returns ─────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 1.5, px: 4 }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(64,121,141,0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: ACCENT,
                borderRadius: 4,
                transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: ACCENT, fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 600 }}>
          {progressPercent}%
        </Typography>
        {progressMessage && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontFamily: FONT_FAMILY_PRIMARY,
              fontSize: '12px',
              textAlign: 'center',
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {progressMessage}
          </Typography>
        )}
        <RotatingStatusMessage variant="dashboard" sx={{ fontSize: '11px', mt: 0.5 }} />
      </Box>
    );
  }
  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Project ID is required. Open a project from Home or Cards.</Alert>
      </Box>
    );
  }
  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No dashboard data available.</Alert>
      </Box>
    );
  }

  /* ─── Render ────────────────────────────────────────────────────────────── */

  const hasFilters = dashboardData.filters?.length > 0;
  const activeFilterCount = Object.values(appliedFilters).filter((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return !!(v.from || v.to || v.fromDate || v.toDate);
    return String(v).trim() !== '';
  }).length;

  const dashboardsExplorerHref = (folderId) => {
    const q = new URLSearchParams();
    if (folderId != null && folderId !== '') q.set('folderId', folderId);
    if (myOnlyDashboardAccess || searchParams.get('scope') === 'my') q.set('scope', 'my');
    const qs = q.toString();
    return qs ? `/dashboards?${qs}` : '/dashboards';
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ mt: GLOBAL_SPACING.SM, mb: GLOBAL_SPACING.LG, pl: GLOBAL_MARGINS.PAGE_MARGIN_SMALL, pr: GLOBAL_MARGINS.PAGE_MARGIN_SMALL }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }} aria-label="folder breadcrumb">
          {folderBreadcrumbSegments.map((segment) => {
            const to =
              segment.id == null ? dashboardsExplorerHref(null) : dashboardsExplorerHref(segment.id);
            return (
              <Link key={segment.id ?? 'root'} component={RouterLink} to={to} underline="hover" color="inherit">
                {segment.label}
              </Link>
            );
          })}
          <Typography color="text.primary">{dashboardName || 'Dashboard'}</Typography>
        </Breadcrumbs>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {isEditingName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <TextField
                    value={tempDashboardName}
                    onChange={handleTempDashboardNameChange}
                    placeholder="Dashboard name"
                    size="small"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !dashboardNameExists && !isCheckingDashboardName) handleSaveName(); if (e.key === 'Escape') handleCancelEdit(); }}
                    autoFocus
                    error={dashboardNameExists}
                    helperText={
                      dashboardNameExists
                        ? 'A dashboard with this name already exists in this tenant'
                        : isCheckingDashboardName
                        ? 'Checking name availability...'
                        : ''
                    }
                    FormHelperTextProps={{
                      sx: { color: dashboardNameExists ? '#d32f2f' : 'text.secondary', ml: 0 },
                    }}
                  />
                  <IconButton size="small" onClick={handleSaveName} disabled={dashboardNameExists || isCheckingDashboardName || !tempDashboardName.trim()} sx={{ color: 'rgb(64, 121, 141)' }}><SaveIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={handleCancelEdit} sx={{ color: 'rgb(64, 121, 141)' }}><CancelIcon fontSize="small" /></IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 'bold', color: 'rgb(64, 121, 141)', fontSize: '18px' }}>{dashboardName || `Dashboard for project`}</Typography>
                  <IconButton size="small" onClick={handleEditName} sx={{ color: 'rgb(64, 121, 141)' }} title="Edit dashboard name"><EditIcon fontSize="small" /></IconButton>
                  {currentProject?.SourceSurveyId && (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={handleSyncResponses}
                      disabled={isSyncing}
                      startIcon={isSyncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon fontSize="small" />}
                      sx={{ borderRadius: '20px', textTransform: 'none', ml: 2, borderColor: 'rgb(64, 121, 141)', color: 'rgb(64, 121, 141)' }}
                    >
                      {isSyncing ? 'Syncing...' : 'Sync Latest Responses'}
                    </Button>
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {!isEditingName && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={pdfExporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                    onClick={handleDownloadPdf}
                    disabled={pdfExporting || allWidgets.length === 0}
                    sx={{ textTransform: 'none', borderColor: ACCENT, color: ACCENT, mr: 0.5 }}
                  >
                    {pdfExporting ? 'Generating PDF…' : 'Download as PDF'}
                  </Button>
                )}
                <Tooltip title="Auto-arrange cards">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleAutoArrange}
                      disabled={allWidgets.length === 0}
                      sx={{ color: ACCENT }}
                      aria-label="Auto-arrange dashboard cards"
                    >
                      <ViewQuiltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {hasFilters && (
                  <Tooltip title="Filters">
                    <Badge
                      badgeContent={activeFilterCount}
                      invisible={activeFilterCount === 0}
                      overlap="circular"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: 18,
                          height: 18,
                          backgroundColor: ACCENT,
                          color: '#fff',
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={openFilterMenu}
                        sx={{ color: ACCENT }}
                        aria-label="Open filters"
                        aria-haspopup="true"
                        aria-expanded={Boolean(filterAnchorEl)}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                )}
                <Tooltip title="Edit cards">
                  <IconButton onClick={handleEditDashboard} size="small" sx={{ color: ACCENT }} aria-label="Edit dashboard cards">
                    <EditNoteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {showProgress && !loading && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercent}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(64,121,141,0.08)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: ACCENT,
                          borderRadius: 3,
                          transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: ACCENT, fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 600, fontSize: '12px', flexShrink: 0, minWidth: 40, textAlign: 'right' }}>
                    {progressPercent}%
                  </Typography>
                </Box>
                {progressMessage && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: 'text.secondary',
                      fontFamily: FONT_FAMILY_PRIMARY,
                      fontSize: '11px',
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  >
                    {progressMessage}
                  </Typography>
                )}
                <RotatingStatusMessage
                  variant={isFiltering ? 'filters' : 'execute'}
                  sx={{ textAlign: 'left', fontSize: '11px', mt: 0.25 }}
                />
              </Box>
            )}
            {saveStatus === 'saved' && (
              <Typography variant="caption" sx={{ color: 'success.main', fontSize: '10px', fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 600 }}>
                Layout saved ✓
              </Typography>
            )}
            {saveStatus === 'error' && (
              <Typography variant="caption" sx={{ color: 'error.main', fontSize: '10px', fontFamily: FONT_FAMILY_PRIMARY }}>
                Save failed
              </Typography>
            )}

            {/* Grid or empty state */}
        {allWidgets.length > 0 ? (
          <>
            <Box
              ref={widgetsPdfRootRef}
              sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
            >
            <div
              ref={gridContainerRef}
              style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <GridLayout
                key={`${projectId}|${widgetLayoutSig}`}
                width={gridLayoutWidth}
                layout={effectiveGridLayout}
                cols={12}
                rowHeight={150}
                margin={GRID_MARGIN}
                containerPadding={[0, 0]}
                onLayoutChange={handleLayoutChange}
                onDragStop={handleDragStop}
                onResizeStop={handleResizeStop}
                draggableHandle=".dash-card__drag-handle"
                useCSSTransforms
              >
                {allWidgets.map((widget, i) => {
                  const key = wKey(widget, i);
                  const cat = wCategory(widget);
                  return (
                    <div key={key} data-widget-key={key} data-dashboard-pdf-widget className={`dash-grid-item dash-grid-item--${cat}`}>
                      {/* Drag handle strip at the top of each card */}
                      <Box className="dash-card__drag-handle" title="Drag to reposition" />
                      {renderWidget(widget)}
                    </div>
                  );
                })}
              </GridLayout>
            </div>
            </Box>

            {/* Section divider labels rendered as non-interactive overlays */}

          </>
        ) : (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed rgba(0,0,0,0.15)' }}>
            <Typography color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
              No widgets configured for this dashboard.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Configure widgets in the Cards section.
            </Typography>
          </Paper>
        )}
          </Grid>
        </Grid>
      </Container>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={closeFilterMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              mt: 0.75,
              boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
              border: '1px solid rgba(0,0,0,0.08)',
              width: 300,
              maxWidth: 'calc(100vw - 24px)',
            },
          },
        }}
      >
        <Box className="dash-filter-popover-inner">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pr: 0.5 }}>
            <Typography sx={{ fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 700, fontSize: '13px', color: '#222' }}>
              Filters
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {activeFilterCount > 0 && (
                <Chip label={`${activeFilterCount} active`} size="small" sx={{ height: 20, fontSize: '10px', bgcolor: `${ACCENT}18`, color: ACCENT, fontWeight: 600 }} />
              )}
              <Tooltip title="Close">
                <IconButton size="small" onClick={closeFilterMenu} sx={{ color: 'text.secondary' }} aria-label="Close filters">
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider sx={{ mb: 1.5 }} />

          {hasFilters ? (
            dashboardData.filters.map((f) => renderFilter(f))
          ) : (
            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', fontFamily: FONT_FAMILY_PRIMARY }}>
              No filters available for this dashboard.
            </Typography>
          )}

          {hasFilters && (
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={applyFilters}
              disabled={isFiltering}
              sx={{
                mt: 2,
                textTransform: 'none',
                fontFamily: FONT_FAMILY_PRIMARY,
                bgcolor: ACCENT,
                borderRadius: 1.5,
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgb(50,100,120)' },
              }}
            >
              {isFiltering ? 'Applying…' : 'Apply Filters'}
            </Button>
          )}
        </Box>
      </Popover>

      {/* ── No-data dialog ────────────────────────────────────────── */}
      <Dialog open={noDataDialogOpen} onClose={() => setNoDataDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 24px 60px rgba(0,0,0,0.18)' } }}
      >
        <DialogTitle sx={{ fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 700, fontSize: '16px' }}>No data for filters</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>{noDataDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoDataDialogOpen(false)} variant="contained"
            sx={{ textTransform: 'none', fontFamily: FONT_FAMILY_PRIMARY, bgcolor: ACCENT, borderRadius: 1.5, '&:hover': { bgcolor: 'rgb(50,100,120)' } }}
          >OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
